import React, { useState } from "react";
import styled from "styled-components";

// InnerLogic Brand Colors
const brandColors = {
  primary: {
    50: "#EBF1FD",
    100: "#D5E2F9", 
    500: "#2D70E2",
    600: "#2254AA",
  },
  secondary: {
    500: "#27CDA7",
    600: "#1D9A7D",
  },
  text: {
    dark: "#2A3039",
    gray: "#666D79",
    light: "#8B93A3",
  },
  background: {
    white: "#FFFFFF",
    lightBlue: "#F8FAFF",
  },
  neutral: {
    200: "#F2F2F2",
    500: "#DFDFDF",
  },
  status: {
    success: "#10B981",
    warning: "#F59E0B",
    error: "#EF4444",
  }
};

const brandGradient = `linear-gradient(135deg, ${brandColors.primary["500"]} 0%, ${brandColors.secondary["500"]} 100%)`;

// Styled Components
const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: "Raleway", sans-serif;
  background: ${brandColors.background.white};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: ${brandColors.background.white};
  box-shadow: 0 1px 3px rgba(45, 112, 226, 0.1);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: ${brandColors.text.gray};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;

  &:hover {
    background: ${brandColors.primary["50"]};
    color: ${brandColors.primary["500"]};
  }
`;

const HeaderTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin: 0;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${brandColors.text.light};
  font-size: 14px;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  gap: 24px;
`;

const Sidebar = styled.div`
  width: 280px;
  background: ${brandColors.background.white};
  border-radius: 12px;
  padding: 24px;
  height: fit-content;
  box-shadow: 0 2px 12px rgba(45, 112, 226, 0.08);
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: ${props => props.active ? brandColors.primary["50"] : 'transparent'};
  color: ${props => props.completed ? brandColors.status.success : props.active ? brandColors.primary["600"] : brandColors.text.gray};
`;

const StepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  background: ${props => 
    props.completed ? brandColors.status.success : 
    props.active ? brandColors.primary["500"] : 
    brandColors.neutral["200"]};
  color: ${props => props.completed || props.active ? 'white' : brandColors.text.gray};
`;

const StepTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin: 0 0 12px 0;
`;

const MainContent = styled.div`
  flex: 1;
  background: ${brandColors.background.white};
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 12px rgba(45, 112, 226, 0.08);
`;

const StepHeader = styled.div`
  margin-bottom: 32px;
`;

const StepHeading = styled.h3`
  font-size: 28px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin: 0 0 8px 0;
`;

const StepDescription = styled.p`
  font-size: 16px;
  color: ${brandColors.text.gray};
  margin: 0;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${brandColors.neutral["200"]};
  border-radius: 8px;
  font-size: 16px;
  font-family: "Raleway", sans-serif;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${brandColors.primary["500"]};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${brandColors.neutral["200"]};
  border-radius: 8px;
  font-size: 16px;
  font-family: "Raleway", sans-serif;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${brandColors.primary["500"]};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${brandColors.neutral["200"]};
  border-radius: 8px;
  font-size: 16px;
  font-family: "Raleway", sans-serif;
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${brandColors.primary["500"]};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: ${brandColors.primary["50"]};
  color: ${brandColors.text.dark};
  font-weight: 600;
  font-size: 14px;
  border-bottom: 2px solid ${brandColors.primary["100"]};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${brandColors.neutral["200"]};
  
  &:hover {
    background: ${brandColors.primary["50"]};
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${brandColors.text.dark};
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const StatusChip = styled.span`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'generated': return brandColors.status.success + '20';
      case 'draft': return brandColors.status.warning + '20';
      case 'sent': return brandColors.primary["50"];
      default: return brandColors.neutral["200"];
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'generated': return brandColors.status.success;
      case 'draft': return brandColors.status.warning;
      case 'sent': return brandColors.primary["600"];
      default: return brandColors.text.gray;
    }
  }};
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${brandColors.neutral["200"]};
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  font-family: "Raleway", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${props => props.primary ? `
    background: ${brandGradient};
    color: white;
    box-shadow: 0 2px 8px rgba(45, 112, 226, 0.15);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(45, 112, 226, 0.25);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: 0 2px 8px rgba(45, 112, 226, 0.15);
    }
  ` : `
    background: transparent;
    color: ${brandColors.text.gray};
    border: 2px solid ${brandColors.neutral["200"]};
    
    &:hover {
      background: ${brandColors.primary["50"]};
      color: ${brandColors.primary["500"]};
      border-color: ${brandColors.primary["200"]};
    }
  `}
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${brandColors.neutral["200"]};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${brandGradient};
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

// Mock data
const mockMembers = [
  { id: 1, name: "Sarah Johnson", department: "Engineering", location: "New York", email: "sarah.j@company.com" },
  { id: 2, name: "Mike Chen", department: "Product", location: "San Francisco", email: "mike.c@company.com" },
  { id: 3, name: "Emily Rodriguez", department: "Marketing", location: "Austin", email: "emily.r@company.com" },
  { id: 4, name: "David Kim", department: "Sales", location: "Chicago", email: "david.k@company.com" },
  { id: 5, name: "Lisa Thompson", department: "HR", location: "Seattle", email: "lisa.t@company.com" },
];

const availableAnchors = [
  { id: "department", name: "Department", description: "Group by department/team" },
  { id: "location", name: "Location", description: "Group by office location" },
  { id: "manager_level", name: "Manager Level", description: "Group by management level" },
  { id: "tenure", name: "Tenure", description: "Group by years at company" },
  { id: "role_type", name: "Role Type", description: "Group by role/function" },
  { id: "performance_band", name: "Performance Band", description: "Group by performance rating" }
];

const WIZARD_STEPS = [
  { id: 1, title: "Create Group", description: "Set up your action planning group" },
  { id: 2, title: "Select People", description: "Choose team members" },
  { id: 3, title: "Anchor Mapping", description: "Configure data anchors" },
  { id: 4, title: "Report Template", description: "Choose report format" },
  { id: 5, title: "Pre-Plan Pulse", description: "Optional diagnostic survey" },
  { id: 6, title: "Launch Group", description: "Activate the planning group" }
];

const ActionPlanWizard = ({ onBack, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    groupName: "",
    description: "",
    selectedMembers: [],
    defaultAnchors: [],
    memberAnchors: {}, // individual member anchor overrides
    reportTemplate: "", // selected report template
    includePulse: false,
    pulseTemplate: ""
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < WIZARD_STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    const newGroup = {
      id: Date.now(),
      name: formData.groupName,
      created_at: new Date().toISOString().split('T')[0],
      member_count: formData.selectedMembers.length,
      plans_sent: 0, // Plans haven't been sent yet since they're generated after wizard
      on_track: 0,
      at_risk: 0,
      off_track: 0
    };
    onComplete(newGroup);
  };

  const renderStep1 = () => (
    <>
      <StepHeader>
        <StepHeading>Create Action Planning Group</StepHeading>
        <StepDescription>
          Set up the basic details for your action planning group. This will be the container for all related action plans.
        </StepDescription>
      </StepHeader>

      <FormGroup>
        <Label>Group Name</Label>
        <Input
          type="text"
          placeholder="e.g., Q1 Leadership Development"
          value={formData.groupName}
          onChange={(e) => updateFormData('groupName', e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>Description</Label>
        <TextArea
          placeholder="Brief description of the group's purpose and goals..."
          value={formData.description}
          onChange={(e) => updateFormData('description', e.target.value)}
        />
      </FormGroup>
    </>
  );

  const renderStep2 = () => (
    <>
      <StepHeader>
        <StepHeading>Select Team Members</StepHeading>
        <StepDescription>
          Choose the managers who will receive action plans. You can filter by department, location, or other criteria.
        </StepDescription>
      </StepHeader>

      <Table>
        <thead>
          <tr>
            <TableHeader>
              <Checkbox
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFormData('selectedMembers', mockMembers.map(m => m.id));
                  } else {
                    updateFormData('selectedMembers', []);
                  }
                }}
              />
              Select All
            </TableHeader>
            <TableHeader>Name</TableHeader>
            <TableHeader>Department</TableHeader>
            <TableHeader>Location</TableHeader>
          </tr>
        </thead>
        <tbody>
          {mockMembers.map(member => (
            <TableRow key={member.id}>
              <TableCell>
                <Checkbox
                  type="checkbox"
                  checked={formData.selectedMembers.includes(member.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateFormData('selectedMembers', [...formData.selectedMembers, member.id]);
                    } else {
                      updateFormData('selectedMembers', formData.selectedMembers.filter(id => id !== member.id));
                    }
                  }}
                />
              </TableCell>
              <TableCell>{member.name}</TableCell>
              <TableCell>{member.department}</TableCell>
              <TableCell>{member.location}</TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>

      <p style={{ color: brandColors.text.gray, fontSize: '14px' }}>
        Selected: {formData.selectedMembers.length} of {mockMembers.length} members
      </p>
    </>
  );

  const renderStep3 = () => {
    const toggleDefaultAnchor = (anchorId) => {
      const currentAnchors = formData.defaultAnchors;
      if (currentAnchors.includes(anchorId)) {
        updateFormData('defaultAnchors', currentAnchors.filter(id => id !== anchorId));
      } else if (currentAnchors.length < 2) {
        updateFormData('defaultAnchors', [...currentAnchors, anchorId]);
      }
    };

    const updateMemberAnchors = (memberId, anchors) => {
      updateFormData('memberAnchors', {
        ...formData.memberAnchors,
        [memberId]: anchors
      });
    };

    const getMemberAnchors = (memberId) => {
      return formData.memberAnchors[memberId] || formData.defaultAnchors;
    };

    return (
      <>
        <StepHeader>
          <StepHeading>Configure Data Anchors</StepHeading>
          <StepDescription>
            Set default data anchors for the group (up to 2), then customize individual member access as needed.
          </StepDescription>
        </StepHeader>

        <Section>
          <SectionTitle>Default Anchors (Select up to 2)</SectionTitle>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '16px', 
            marginTop: '16px' 
          }}>
            {availableAnchors.map(anchor => (
              <div
                key={anchor.id}
                style={{
                  padding: '16px',
                  border: `2px solid ${formData.defaultAnchors.includes(anchor.id) ? brandColors.primary["500"] : brandColors.neutral["200"]}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: formData.defaultAnchors.includes(anchor.id) ? brandColors.primary["50"] : brandColors.background.white,
                  transition: 'all 0.2s ease',
                  opacity: !formData.defaultAnchors.includes(anchor.id) && formData.defaultAnchors.length >= 2 ? 0.5 : 1
                }}
                onClick={() => toggleDefaultAnchor(anchor.id)}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginBottom: '8px' 
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    background: formData.defaultAnchors.includes(anchor.id) ? brandColors.primary["500"] : brandColors.neutral["200"],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {formData.defaultAnchors.includes(anchor.id) ? '✓' : ''}
                  </div>
                  <strong style={{ color: brandColors.text.dark }}>{anchor.name}</strong>
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: brandColors.text.gray, 
                  lineHeight: '1.4' 
                }}>
                  {anchor.description}
                </div>
              </div>
            ))}
          </div>
          
          {formData.defaultAnchors.length > 0 && (
            <div style={{ 
              marginTop: '16px', 
              padding: '12px 16px', 
              background: brandColors.primary["50"], 
              borderRadius: '8px',
              border: `1px solid ${brandColors.primary["100"]}`
            }}>
              <strong style={{ color: brandColors.primary["600"] }}>Selected: </strong>
              {formData.defaultAnchors.map(anchorId => {
                const anchor = availableAnchors.find(a => a.id === anchorId);
                return anchor?.name;
              }).join(' + ')}
              <div style={{ fontSize: '14px', color: brandColors.text.gray, marginTop: '4px' }}>
                This combination will isolate data for each unique pairing of these anchors.
              </div>
            </div>
          )}
        </Section>

        {formData.selectedMembers.length > 0 && formData.defaultAnchors.length > 0 && (
          <Section style={{ marginTop: '32px' }}>
            <SectionTitle>Individual Member Customization</SectionTitle>
            <Table style={{ marginTop: '16px' }}>
              <thead>
                <tr>
                  <TableHeader>Member</TableHeader>
                  <TableHeader>Department</TableHeader>
                  <TableHeader>Location</TableHeader>
                  <TableHeader>Data Anchors</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </tr>
              </thead>
              <tbody>
                {formData.selectedMembers.map(memberId => {
                  const member = mockMembers.find(m => m.id === memberId);
                  const memberAnchors = getMemberAnchors(memberId);
                  const isCustomized = formData.memberAnchors[memberId];
                  
                  return (
                    <TableRow key={memberId}>
                      <TableCell>
                        <strong>{member?.name}</strong>
                      </TableCell>
                      <TableCell>{member?.department}</TableCell>
                      <TableCell>{member?.location}</TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {memberAnchors.map(anchorId => {
                            const anchor = availableAnchors.find(a => a.id === anchorId);
                            return (
                              <StatusChip 
                                key={anchorId} 
                                status={isCustomized ? "at-risk" : "on-track"}
                              >
                                {anchor?.name}
                              </StatusChip>
                            );
                          })}
                        </div>
                        {isCustomized && (
                          <div style={{ fontSize: '12px', color: brandColors.status.warning, marginTop: '4px' }}>
                            Customized
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button style={{ padding: '6px 12px', fontSize: '12px', marginRight: '8px' }}>
                          Customize
                        </Button>
                        {isCustomized && (
                          <Button 
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => updateMemberAnchors(memberId, formData.defaultAnchors)}
                          >
                            Reset
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </tbody>
            </Table>
          </Section>
        )}
      </>
    );
  };

  const renderStep4 = () => {
    const reportTemplates = [
      {
        id: "custom-action-plan",
        title: "Custom Action Plan Report",
        description: "Personalized action plans with specific recommendations based on individual data and goals. Perfect for targeted development initiatives."
      },
      {
        id: "summary-report", 
        title: "Summary Report",
        description: "High-level overview of group performance with key insights and trends. Ideal for leadership dashboards and executive briefings."
      },
      {
        id: "trend-report",
        title: "Trend Report", 
        description: "Detailed analysis of performance trends over time with predictive insights. Best for ongoing monitoring and strategic planning."
      }
    ];

    return (
      <>
        <StepHeader>
          <StepHeading>Select Report Template</StepHeading>
          <StepDescription>
            Choose the type of report that will be generated for your action planning group. Each template provides different insights and formatting.
          </StepDescription>
        </StepHeader>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '20px', 
          marginTop: '24px' 
        }}>
          {reportTemplates.map(template => (
            <div
              key={template.id}
              style={{
                padding: '24px',
                border: `2px solid ${formData.reportTemplate === template.id ? brandColors.primary["500"] : brandColors.neutral["200"]}`,
                borderRadius: '12px',
                cursor: 'pointer',
                background: formData.reportTemplate === template.id ? brandColors.primary["50"] : brandColors.background.white,
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onClick={() => updateFormData('reportTemplate', template.id)}
            >
              {formData.reportTemplate === template.id && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: brandColors.primary["500"],
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  ✓
                </div>
              )}
              
              <h4 style={{ 
                margin: '0 0 12px 0', 
                color: brandColors.text.dark,
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {template.title}
              </h4>
              
              <p style={{ 
                margin: 0, 
                color: brandColors.text.gray,
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                {template.description}
              </p>
            </div>
          ))}
        </div>

        {formData.reportTemplate && (
          <div style={{ 
            marginTop: '24px', 
            padding: '16px', 
            background: brandColors.primary["50"], 
            borderRadius: '8px',
            border: `1px solid ${brandColors.primary["100"]}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: brandColors.primary["600"] }}>
                Selected: {reportTemplates.find(t => t.id === formData.reportTemplate)?.title}
              </span>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderStep5 = () => (
    <>
      <StepHeader>
        <StepHeading>Pre-Plan Pulse (Optional)</StepHeading>
        <StepDescription>
          Send a diagnostic pulse survey before generating plans to gather additional context and insights.
        </StepDescription>
      </StepHeader>

      <FormGroup>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <Checkbox
            type="checkbox"
            checked={formData.includePulse}
            onChange={(e) => updateFormData('includePulse', e.target.checked)}
          />
          <Label style={{ margin: 0 }}>Send diagnostic pulse before generating plans</Label>
        </div>
      </FormGroup>

      {formData.includePulse && (
        <FormGroup>
          <Label>Pulse Template</Label>
          <Select
            value={formData.pulseTemplate}
            onChange={(e) => updateFormData('pulseTemplate', e.target.value)}
          >
            <option value="">Select a template</option>
            <option value="leadership-assessment">Leadership Assessment</option>
            <option value="team-dynamics">Team Dynamics</option>
            <option value="performance-check">Performance Check-in</option>
            <option value="culture-pulse">Culture Pulse</option>
          </Select>
        </FormGroup>
      )}
    </>
  );

  const renderStep6 = () => (
    <>
      <StepHeader>
        <StepHeading>Launch Planning Group</StepHeading>
        <StepDescription>
          Review your planning group configuration and launch it. Action plans will be generated automatically based on your settings.
        </StepDescription>
      </StepHeader>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <div>
          <h4 style={{ margin: '0 0 16px 0', color: brandColors.text.dark }}>Group Settings</h4>
          <div style={{ 
            background: brandColors.background.white, 
            padding: '16px', 
            borderRadius: '8px',
            border: `1px solid ${brandColors.primary["100"]}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', color: brandColors.text.gray }}>Group name:</span>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>{formData.groupName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', color: brandColors.text.gray }}>Members:</span>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>{formData.selectedMembers.length} people</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', color: brandColors.text.gray }}>Anchors:</span>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>
                {formData.defaultAnchors.map(anchorId => {
                  const anchor = availableAnchors.find(a => a.id === anchorId);
                  return anchor?.name;
                }).join(' + ') || 'None selected'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', color: brandColors.text.gray }}>Report:</span>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>
                {formData.reportTemplate === 'custom-action-plan' ? 'Custom Action Plan' : 
                 formData.reportTemplate === 'summary-report' ? 'Summary Report' :
                 formData.reportTemplate === 'trend-report' ? 'Trend Report' : 'None selected'}
              </span>
            </div>
          </div>
        </div>

        <div>
          <h4 style={{ margin: '0 0 16px 0', color: brandColors.text.dark }}>What happens next?</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ 
                width: '24px', 
                height: '24px', 
                borderRadius: '50%', 
                background: brandColors.primary["500"], 
                color: 'white', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '12px', 
                fontWeight: '600',
                flexShrink: 0,
                marginTop: '2px'
              }}>
                1
              </div>
              <div>
                <div style={{ fontWeight: '600', color: brandColors.text.dark }}>
                  {formData.includePulse ? 'Send pre-plan pulse' : 'Generate action plans'}
                </div>
                <div style={{ fontSize: '14px', color: brandColors.text.gray }}>
                  {formData.includePulse ? 'Diagnostic survey sent to all members' : 'AI creates personalized plans using anchor data'}
                </div>
              </div>
            </div>
            
            {formData.includePulse && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  background: brandColors.secondary["500"], 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '12px', 
                  fontWeight: '600',
                  flexShrink: 0,
                  marginTop: '2px'
                }}>
                  2
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: brandColors.text.dark }}>Generate action plans</div>
                  <div style={{ fontSize: '14px', color: brandColors.text.gray }}>
                    AI creates plans using survey responses + anchor data
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ 
                width: '24px', 
                height: '24px', 
                borderRadius: '50%', 
                background: brandColors.status.success, 
                color: 'white', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '12px', 
                fontWeight: '600',
                flexShrink: 0,
                marginTop: '2px'
              }}>
              {formData.includePulse ? '3' : '2'}
              </div>
              <div>
                <div style={{ fontWeight: '600', color: brandColors.text.dark }}>Plans available for review</div>
                <div style={{ fontSize: '14px', color: brandColors.text.gray }}>
                  Review and approve plans in the dashboard
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px', background: brandColors.primary["50"], borderRadius: '8px' }}>
        <h4 style={{ margin: '0 0 16px 0', color: brandColors.primary["600"] }}>🚀 Ready to Launch</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <strong>{formData.selectedMembers.length}</strong>
            <div style={{ fontSize: '14px', color: brandColors.text.gray }}>Members selected</div>
          </div>
          <div>
            <strong>{formData.defaultAnchors.length}</strong>
            <div style={{ fontSize: '14px', color: brandColors.text.gray }}>Data anchors configured</div>
          </div>
          <div>
            <strong>{formData.reportTemplate ? 'Selected' : 'None'}</strong>
            <div style={{ fontSize: '14px', color: brandColors.text.gray }}>Report template</div>
          </div>
          <div>
            <strong>{formData.includePulse ? 'Yes' : 'No'}</strong>
            <div style={{ fontSize: '14px', color: brandColors.text.gray }}>Pre-plan pulse</div>
          </div>
        </div>
      </div>
    </>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return renderStep1();
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.groupName.trim().length > 0;
      case 2: return formData.selectedMembers.length > 0;
      case 3: return formData.defaultAnchors.length > 0;
      case 4: return formData.reportTemplate.length > 0;
      default: return true;
    }
  };

  return (
    <Container>
      <HeaderSection>
        <BackButton onClick={onBack}>
          <span>←</span>
          Back to Hub
        </BackButton>
        <HeaderTitle>Create Action Planning Group</HeaderTitle>
        <StepIndicator>
          Step {currentStep} of {WIZARD_STEPS.length}
        </StepIndicator>
      </HeaderSection>

      <ContentWrapper>
        <Sidebar>
          <h4 style={{ margin: '0 0 24px 0', color: brandColors.text.dark }}>Progress</h4>
          <StepList>
            {WIZARD_STEPS.map((step) => (
              <StepItem 
                key={step.id} 
                active={step.id === currentStep}
                completed={step.id < currentStep}
              >
                <StepNumber 
                  active={step.id === currentStep}
                  completed={step.id < currentStep}
                >
                  {step.id < currentStep ? '✓' : step.id}
                </StepNumber>
                <div>
                  <StepTitle>{step.title}</StepTitle>
                  <div style={{ fontSize: '12px', color: brandColors.text.light, marginTop: '2px' }}>
                    {step.description}
                  </div>
                </div>
              </StepItem>
            ))}
          </StepList>
        </Sidebar>

        <MainContent>
          {renderCurrentStep()}

          <ButtonGroup>
            <Button onClick={handlePrevious} disabled={currentStep === 1}>
              Previous
            </Button>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              {currentStep === WIZARD_STEPS.length ? (
                <Button primary onClick={handleComplete} disabled={!canProceed()}>
                  🚀 Launch Planning Group
                </Button>
              ) : (
                <Button primary onClick={handleNext} disabled={!canProceed()}>
                  Next Step
                </Button>
              )}
            </div>
          </ButtonGroup>
        </MainContent>
      </ContentWrapper>
    </Container>
  );
};

export default ActionPlanWizard; 